import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { BankStore, InsuranceStore } from '../../core/stores/base.store';
import { InsuranceLoan, BankTransaction, InitBankTransaction } from '../../core/models';
import { FuseConfirmationService } from '../../core/services/confirmation/confirmation.service';
import { NotificationUtils } from '../../core/utils';
import { NumberFormatDirective } from '../../core/utils/comma.utils';
import { LoanUtils } from '../../core/utils/loan.utils';

@Component({
  selector: 'app-insurance-loan',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatCheckboxModule,
    MatCardModule,
    NumberFormatDirective
  ],
  templateUrl: './insurance-loan.component.html',
  styleUrls: ['./insurance-loan.component.css']
})
export class InsuranceLoanComponent implements OnInit {
  private insuranceStore = inject(InsuranceStore);
  private bankStore = inject(BankStore);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  #fuseConfirmationService = inject(FuseConfirmationService);

  constructor() {
    // Create an effect to watch for selectedLoan changes and create transactions when needed
    effect(() => {
      const selectedLoan = this.insuranceStore.selectedLoan();
      
      // Only proceed if we have a selected loan and the newLoanCreated flag is true
      if (selectedLoan && this.newLoanCreated()) {
        // Reset the flag immediately to prevent multiple executions
        this.newLoanCreated.set(false);
        
        // Create the initial bank transaction
        this.createInitialBankTransaction(selectedLoan);
        this.router.navigate(['/insurance']);
      }
    });
  }

  selectedInsurance = computed(() => this.insuranceStore.selected());
  selectedLoan = computed(() => this.insuranceStore.selectedLoan());
  banks = computed(() => this.bankStore.entities());
  newLoanCreated = signal(false); // Flag to track if a new loan was created

  // State options for the dropdown
  stateOptions = [
    { value: 'active', label: 'Active' },
    { value: 'paid_off', label: 'Paid Off' },
    { value: 'pending', label: 'Pending' },
  ];

  isEdit = false;

  form: FormGroup = this.fb.group({
    id: [null],
    name: ['', Validators.required],
    insurance_id: [null],
    bank_id: [null, Validators.required],
    state: ['active', Validators.required],
    amount: ['', [Validators.required, Validators.min(0)]],
    interest_fee: ['', [Validators.required, Validators.min(0)]],
    close_date: ['', Validators.required],
    fundToBank: [true] // Default to true for convenience
  });

  ngOnInit() {
    if (!this.selectedInsurance()) {
      this.router.navigate(['/insurance']);
    }
    // Add conditional validation for bank_id based on fundToBank value
    this.form.get('fundToBank')?.valueChanges.subscribe(fundToBank => {
      const bankIdControl = this.form.get('bank_id');
      if (fundToBank) {
        bankIdControl?.setValidators([Validators.required]);
      } else {
        bankIdControl?.clearValidators();
      }
      bankIdControl?.updateValueAndValidity();
    });
    
    // Trigger initial validation based on default fundToBank value
    if (this.form.get('fundToBank')?.value) {
      this.form.get('bank_id')?.setValidators([Validators.required]);
    } else {
      this.form.get('bank_id')?.clearValidators();
    }
    this.form.get('bank_id')?.updateValueAndValidity();
    
    if (this.selectedLoan()) {
      this.isEdit = true;
      this.form.patchValue(this.selectedLoan());
    }
  }

  onSubmit() {
    if (this.form.valid) {
      try {
        const formValue = this.form.getRawValue();
        formValue.insurance_id = +this.selectedInsurance()?.id;
        formValue.bank_id = +formValue.bank_id;
        
        // Handle close_date formatting
        if (formValue.close_date) {
          try {
            // If it's a Date object, format it properly
            if (formValue.close_date instanceof Date) {
              formValue.close_date = formValue.close_date.toISOString().split('T')[0];
            } else if (typeof formValue.close_date === 'string') {
              // If it's already a string, ensure it's in the right format
              formValue.close_date = formValue.close_date.includes('T') 
                ? formValue.close_date.split('T')[0] 
                : formValue.close_date;
            }
          } catch (error) {
            console.error('Error formatting close_date:', error);
            formValue.close_date = null;
          }
        }

        // Convert string values with commas to numbers
        if (typeof formValue.amount === 'string') {
          formValue.amount = LoanUtils.parseFormattedNumber(formValue.amount);
        }
        
        if (typeof formValue.interest_fee === 'string') {
          formValue.interest_fee = LoanUtils.parseFormattedNumber(formValue.interest_fee);
        }

        // Calculate current_balance as amount + interest_fee
        formValue.current_balance = Number(formValue.amount) + Number(formValue.interest_fee);

        // Check if we should create a transaction for funding to bank
        const fundToBank = this.form.get('fundToBank')?.value === true;
        const isNewActive = !this.isEdit && formValue.state === 'active';
        const isPendingToActive = this.isEdit && this.selectedLoan()?.state === 'pending' && formValue.state === 'active';

        // Set the flag if this is a new active loan or a loan changing from pending to active
        if (fundToBank && (isNewActive || isPendingToActive)) {
          this.newLoanCreated.set(true);
        }
        delete formValue.fundToBank

        if (this.isEdit) {
          // Update existing loan
          this.insuranceStore.updateLoan({
            loan: formValue, 
            endpoint: 'insurance_loan', 
            collectionName: 'insurance_loans_collection'
          });
          // Navigate immediately for edits
          this.router.navigate(['/insurance']);
        } else {
          // Create new loan
          delete formValue.id;
          this.insuranceStore.createLoan({
            loan: formValue,
            endpoint: 'insurance_loan', 
            collectionName: 'insurance_loans_collection'
          });
        }
      } catch (error) {
        console.error('Error in insurance loan submission:', error);
        NotificationUtils.getInstance().showError('Failed to process insurance loan');
      }
    }
  }

  onCancel() {
    this.router.navigate(['/insurance']);
  }

  /**
   * Creates an initial bank transaction for a newly created insurance loan
   * This is triggered by the effect when a new loan is created and the newLoanCreated flag is true
   * @param loan The newly created insurance loan
   */
  createInitialBankTransaction(loan: InsuranceLoan) {
    
    if (!loan || !loan.id) {
      console.error('Invalid loan provided to createInitialBankTransaction');
      return;
    }

    const fundToBank = this.form.get('fundToBank')?.value === true;
    const today = this.formatDateAsYYYYMMDD(new Date());
    
    const transaction: Omit<BankTransaction, 'id'> = {
      ...InitBankTransaction,
      bank_id: loan.bank_id,
      contact_id: this.selectedInsurance()?.contact_id, 
      loan_id: null, 
      insurance_id: loan.insurance_id,
      insurance_loan_id: loan.id,
      category: 'Insurance loan',
      amount: Number(loan.amount), // Positive amount for funds to bank
      principal: 0,
      interest: 0,
      late_fee: 0,
      transaction_date: this.formatDateAsYYYYMMDD(new Date(loan.close_date))
    };
    delete (transaction as any).id;

    // Create the bank transaction
    this.bankStore.createChild({
      child: transaction,
      endpoint: 'bank_transaction',
      collectionName: 'bank_transactions_collection'
    })
    this.insuranceStore.selectLoan(null);
  }

  /**
   * Format a date as YYYY-MM-DD
   * @param date The date to format
   * @returns The formatted date string
   */
  private formatDateAsYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
